// frontend/src/components/layout/PageLayout.tsx
import React from 'react';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';

interface PageLayoutProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
  showRoleIndicator?: boolean;
  error?: string | null;
  headerClassName?: string;
  contentClassName?: string;
  compact?: boolean; // For mobile-friendly compact mode
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  actions,
  children,
  className,
  loading = false,
  showRoleIndicator = false,
  error = null,
  headerClassName,
  contentClassName,
  compact = false
}) => {
  const { user } = useAuthStore();
  const isImplementor = user?.role === 'implementor';

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Page Header - Responsive */}
      <div className={cn(
        'flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
        compact ? 'px-3 py-2 sm:px-4 sm:py-3' : 'px-4 sm:px-6 py-3 sm:py-4',
        headerClassName
      )}>
        <div className={cn(
          'flex items-center justify-between',
          compact ? 'gap-2' : 'gap-3'
        )}>
          {/* Title Section */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="min-w-0 flex-1">
                <h1 className={cn(
                  'font-bold text-gray-900 dark:text-white truncate',
                  compact ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl'
                )}>
                  {title}
                </h1>
                {subtitle && !compact && (
                  <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                    {subtitle}
                  </p>
                )}
              </div>

              {/* Role Indicator - Mobile friendly */}
              {showRoleIndicator && isImplementor && (
                <div className="flex items-center space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-md sm:rounded-lg border border-purple-200 dark:border-purple-700 flex-shrink-0">
                  <span className="text-purple-600 dark:text-purple-400 text-xs sm:text-sm font-medium">
                    System Admin
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions Section - Responsive */}
          {actions && (
            <div className="flex items-center justify-end space-x-2 sm:space-x-3 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>

      {/* Page Content - Responsive */}
      <div className={cn(
        'flex-1 overflow-auto bg-gray-50 dark:bg-gray-900',
        contentClassName
      )}>
        {loading ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">Loading...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center max-w-md w-full">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 dark:text-red-400 font-medium mb-2 text-sm sm:text-base">Error</p>
                <p className="text-red-700 dark:text-red-300 text-xs sm:text-sm break-words">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};